import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  MenuItem,
  TextField,
  useTheme,
} from "@mui/material";
import { WorldRenderer } from "../../WorldRenderer";
import { PropValueLine } from "../../common/PropValueLine";
import { SiteAnchorVisual } from "../../../visuals/SiteAnchorVisual";
import { useEffect, useState } from "react";
import { WREvents } from "../../WREvents";
import * as THREE from "three";
import { useNavigate } from "react-router";
import {
  CameraAlt,
  Check,
  Download,
  GridOn,
  Save,
  ToggleOff,
  ToggleOn,
  TransferWithinAStation,
} from "@mui/icons-material";
import { MapBuilder } from "../robot/MapBuilder";
import { BaseVisual } from "../../../visuals/BaseVisual";
import { loadRecords } from "../../../api/connection";
import { Visual } from "../../../models/Visual";
import { RenderAccordionToolbar } from "../../../components/Minibar";
import { generateTiles } from "../../../utils/siteAnchorTiles";
import {
  showProgressDialog,
  showSnackbar,
} from "../../../components/UniversalDialog";
import { deleteOrgBlob } from "../../common/UploadBlob";
import { transferToOrg } from "../../../utils/orgUtils";
export function SiteProps({
  wr,
  site,
  pinned,
  panel,
  togglePin,
}: {
  wr: WorldRenderer;
  site: SiteAnchorVisual;
  pinned: Set<string>;
  panel: string;
  togglePin: (panel: string) => void;
}) {
  const theme = useTheme();
  const [siteState, setSiteState] = useState<SiteAnchorVisual>(site);
  const [siteVisuals, setSiteVisuals] = useState<Visual[]>([]);
  const [map, setMap] = useState<THREE.Mesh | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [envData, setEnvData] = useState({
    cameraVector: { x: 0, y: 0, z: 0 },
    hit: false,
  });
  useEffect(() => {
    const siteUpdateListener = async (site: SiteAnchorVisual) => {
      const svs = await loadRecords<Visual>("visuals", {
        siteAnchorId: site._id,
      });
      setSiteVisuals(svs);
      setSiteState({ ...site } as SiteAnchorVisual);
    };
    wr.onEvent(WREvents.SiteUpdated, siteUpdateListener);
    if (site) {
      siteUpdateListener(site).catch(console.error);
    }
    return () => {
      wr.off(WREvents.SiteUpdated, siteUpdateListener);
    };
  }, [wr, site]);
  useEffect(() => {
    setSiteState(site);
  }, [site]);
  return (
    <>
      <Backdrop
        open={loading}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <RenderAccordionToolbar
        accordionToolbarItems={[
          {
            tooltip: "Save",
            icon: <Save />,
            onClick: async () => {
              Object.assign(site, siteState);
              site.updateObj();
              await site.save();
              wr.emitEvent(WREvents.SitesUpdated, wr.dataService.sites);
            },
          },
          {
            tooltip: site.active ? "Deactivate" : "Activate",
            icon: site.active ? (
              <ToggleOn color="success" />
            ) : (
              <ToggleOff color="disabled" />
            ),
            onClick: async () => {
              if (site.active) {
                wr.planetService.tileRenderingOnOff(true);
                wr.dataService.deactivateSite();
                navigate(`/sites/all`);
              } else {
                navigate(`/sites/${site._id}`);
              }
            },
          },
          ...(site.active
            ? [
                {
                  tooltip: "Fix Camera",
                  icon: <CameraAlt />,
                  onClick: async () => {
                    const up = new THREE.Vector3(0, 0, 1);
                    up.applyQuaternion(site.obj.quaternion);
                    // const worldPos = site.obj.getWorldPosition(new THREE.Vector3());

                    wr.setCameraPosition({
                      position: up.clone().multiplyScalar(50),
                      target: new THREE.Vector3(0, 0, 0),
                      up,
                    });
                  },
                },
                {
                  tooltip: "Build Grid",
                  icon: <GridOn />,
                  onClick: async () => {
                    await wr.gridService.buildGrid();
                  },
                },
              ]
            : []),
          {
            tooltip: "Download Tiles",
            icon: <Download />,
            onClick: async () => {
              setLoading(true);
              if (!site) {
                console.warn("No site available.");
                setLoading(false);
                return;
              }
              let progressDialog: ReturnType<typeof showProgressDialog> | null =
                null;

              progressDialog = showProgressDialog(
                "Tiles Progress",
                "Deleting any existing tiles...",
                0
              ) as ReturnType<typeof showProgressDialog>;
              if (site.tileBlobId) {
                await deleteOrgBlob(site.tileBlobId, "techmage-tiles");
                progressDialog.setProgress(100);
              }
              progressDialog.updateText("Generating new tiles...");
              await generateTiles(site, progressDialog.setProgress, setLoading);
              if (progressDialog) {
                progressDialog.onComplete(false, "");
              }
              showSnackbar("Tiles generated successfully", "success");
            },
          },
        ]}
        pinned={pinned}
        panel={panel}
        togglePin={togglePin}
      />
      <Box>
        <PropValueLine
          label="ID"
          value={
            <TextField
              size="small"
              fullWidth
              variant="standard"
              value={siteState.id}
              onChange={(e) => {
                const update = {
                  ...siteState,
                  id: e.target.value,
                } as SiteAnchorVisual;
                setSiteState(update);
              }}
            />
          }
        />
        <PropValueLine
          label="Grid"
          value={
            <>
              <TextField
                size="small"
                type="number"
                sx={{ width: "80px" }}
                variant="standard"
                value={siteState.grid.size}
                onChange={(e) => {
                  const update = {
                    ...siteState,
                    grid: {
                      ...siteState.grid,
                      size: parseFloat(e.target.value),
                    },
                  } as SiteAnchorVisual;
                  setSiteState(update);
                }}
              />
              <span style={{ margin: "0 10px" }}>by</span>
              <TextField
                size="small"
                type="number"
                sx={{ width: "80px" }}
                variant="standard"
                value={siteState.grid.resolution}
                onChange={(e) => {
                  const update = {
                    ...siteState,
                    grid: {
                      ...siteState.grid,
                      resolution: parseFloat(e.target.value),
                    },
                  } as SiteAnchorVisual;
                  setSiteState(update);
                }}
              />
              {siteState.grid.blobId && <Check color="success" />}
            </>
          }
        />

        <PropValueLine label="DBID" value={site._id} />
        <PropValueLine label="MGRS" value={site.mgrs} />
        <PropValueLine label="Lat" value={site.location.lat.toFixed(6)} />
        <PropValueLine label="Lon" value={site.location.lon.toFixed(6)} />
        <PropValueLine
          label="Alt"
          value={
            <TextField
              size="small"
              fullWidth
              variant="standard"
              value={siteState.location.alt}
              onChange={(e) => {
                const update = {
                  ...siteState,
                  location: {
                    ...siteState.location,
                    alt: parseFloat(e.target.value),
                  },
                } as SiteAnchorVisual;
                setSiteState(update);
              }}
            />
          }
        />
        {site.active && (
          <>
            <PropValueLine
              label="Show Box"
              value={
                <Checkbox
                  size="small"
                  checked={siteState.showBox}
                  onChange={(e, checked) => {
                    const update = {
                      ...siteState,
                      showBox: checked,
                    } as SiteAnchorVisual;
                    setSiteState(update);
                    site.showBox = checked;
                    site.updateObj();
                  }}
                />
              }
            />
            <PropValueLine
              label="Show Grid"
              value={
                <Checkbox
                  size="small"
                  checked={siteState.showGrid}
                  onChange={(e, checked) => {
                    const update = {
                      ...siteState,
                      showGrid: checked,
                    } as SiteAnchorVisual;
                    setSiteState(update);
                    site.showGrid = checked;
                    site.updateObj();
                  }}
                />
              }
            />

            <PropValueLine
              label="Show Map"
              value={
                <Checkbox
                  size="small"
                  checked={map !== null}
                  onChange={async (e, checked) => {
                    if (checked) {
                      const mapBuilder = new MapBuilder(site);
                      const map = await mapBuilder.build();
                      const mesh = await mapBuilder.previewMap(map);
                      setMap(mesh);
                    } else if (map) {
                      map.removeFromParent();
                      setMap(null);
                    }
                  }}
                />
              }
            />
            <PropValueLine
              label="Rep Visual"
              value={
                <TextField
                  size="small"
                  select
                  variant="standard"
                  value={siteState.representingVisualId}
                  onChange={(e) => {
                    const update = {
                      ...siteState,
                      representingVisualId: e.target.value,
                    } as SiteAnchorVisual;
                    setSiteState(update);
                  }}
                >
                  <MenuItem value="">None</MenuItem>
                  {siteVisuals.map((v) => (
                    <MenuItem key={v._id} value={v._id}>
                      {v.id || v.type || v._id}
                    </MenuItem>
                  ))}
                </TextField>
              }
            />
          </>
        )}
        {/* <PropValueLine label='FRCAM' value={`[${envData.cameraVector.x.toFixed(2)}, ${envData.cameraVector.y.toFixed(2)}, ${envData.cameraVector.z.toFixed(2)}, ${envData.hit} ]`} /> */}
        {/* <Box>
          <Button
            color="secondary"
            onClick={async () => {
              Object.assign(site, siteState);
              site.updateObj();
              await site.save();
              wr.emitEvent(WREvents.SitesUpdated, wr.dataService.sites);
            }}
          >
            SAVE
          </Button>
          <Button
            size="small"
            color="secondary"
            onClick={async () => {
              if (site.active) {
                wr.planetService.tileRenderingOnOff(true);
                wr.dataService.deactivateSite();
                navigate(`/sites/all`);
              } else {
                navigate(`/sites/${site._id}`);
              }
            }}
          >
            {site.active ? "Deactivate" : "Activate"}
          </Button>

          {site.active && (
            <>
              <Button
                size="small"
                color="secondary"
                onClick={async () => {
                  const up = new THREE.Vector3(0, 0, 1);
                  up.applyQuaternion(site.obj.quaternion);
                  // const worldPos = site.obj.getWorldPosition(new THREE.Vector3());

                  wr.setCameraPosition({
                    position: up.clone().multiplyScalar(50),
                    target: new THREE.Vector3(0, 0, 0),
                    up,
                  });
                }}
              >
                FIX CAMERA
              </Button>

              <Button
                size="small"
                color="secondary"
                onClick={async () => {
                  await wr.gridService.buildGrid();
                }}
              >
                Build Grid
              </Button>
            </>
          )}
        </Box> */}
      </Box>
    </>
  );
}
